import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { LookupTypeCodes } from '../../../models/lookup.models';
import {
  SYSTEM_DEFAULT_VISIBILITY_CODE,
  VisibilityModeOption,
} from '../../../models/user.models';
import { LookupService } from '../../../services/lookup.service';
import { ErrorStateComponent } from '../error-state/error-state.component';

/** Seeds the Resource Visibility dialog. */
export interface ResourceVisibilityDialogData {
  /** Resource display name shown in the header. */
  userName: string;
  /**
   * The currently selected option code: SYSTEM_DEFAULT_VISIBILITY_CODE (the system
   * default) or one of the RESOURCE_VISIBILITY_MODE lookup codes. Seeds the radio group.
   */
  currentSelectionCode: string;
}

/**
 * Lets an admin set a per-resource visibility policy (or leave it at the system
 * default). Closing with Save returns the chosen code to the caller, which
 * persists it (PUT for an explicit mode, DELETE to fall back to the default);
 * Cancel/close returns undefined.
 */
@Component({
  selector: 'app-resource-visibility-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    ErrorStateComponent,
  ],
  templateUrl: './resource-visibility-dialog.component.html',
  styleUrls: ['./resource-visibility-dialog.component.scss'],
})
export class ResourceVisibilityDialogComponent implements OnInit {
  private dialogRef =
    inject<MatDialogRef<ResourceVisibilityDialogComponent, string>>(
      MatDialogRef,
    );
  private lookupService = inject(LookupService);
  private destroyRef = inject(DestroyRef);
  data = inject<ResourceVisibilityDialogData>(MAT_DIALOG_DATA);

  /** The "system default" option (i.e. no explicit per-resource override). */
  readonly inheritCode = SYSTEM_DEFAULT_VISIBILITY_CODE;

  /**
   * The explicit per-resource override options, sourced from the backend
   * RESOURCE_VISIBILITY_MODE lookup (label = value name, description = value
   * description) so the dialog copy stays in sync with the seeded lookup.
   */
  modes: VisibilityModeOption[] = [];

  /** The radio selection (system default, or one of the explicit mode codes). */
  selectedCode: string = this.data.currentSelectionCode;

  // Drives the dialog body: spinner while the lookup loads, an error + retry if
  // it fails (or returns nothing), or the options once they're available.
  loadState: 'loading' | 'error' | 'ready' = 'loading';

  ngOnInit(): void {
    this.loadModes();
  }

  /**
   * Loads the visibility-mode options from the RESOURCE_VISIBILITY_MODE lookup
   * (sorted by sortOrder). Pass `forceRefresh` (the Retry path) to clear the
   * cached lookup first so a re-fetch actually hits the API even after a cached
   * empty/success result; the initial load reuses the cache the users page
   * already populated.
   */
  loadModes(forceRefresh = false): void {
    this.loadState = 'loading';

    if (forceRefresh) {
      this.lookupService.clearCache(LookupTypeCodes.RESOURCE_VISIBILITY_MODE);
    }

    this.lookupService
      .getLookupValues(LookupTypeCodes.RESOURCE_VISIBILITY_MODE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (values) => {
          const modes = values
            .filter((v) => v.isActive)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map<VisibilityModeOption>((v) => ({
              code: v.code,
              label: v.name,
              description: v.description ?? '',
              isDefault: v.isDefault,
            }));

          // No options means the dialog offers nothing to pick — surface a clear
          // error + Retry rather than an empty radio list.
          if (!modes.length) {
            console.error('No resource visibility modes returned from lookup.');
            this.loadState = 'error';
            return;
          }

          this.modes = modes;
          this.loadState = 'ready';
        },
        error: (err) => {
          console.error('Error loading resource visibility modes:', err);
          this.loadState = 'error';
        },
      });
  }

  /**
   * What the system default resolves to (e.g. "Office and Practice Area"), taken from the
   * RESOURCE_VISIBILITY_MODE lookup value flagged `isDefault` so it tracks the backend default
   * without a hardcoded mirror. Falls back to a neutral phrase if no active value is flagged
   * default so the sentence never renders with a missing subject.
   */
  get systemDefaultLabel(): string {
    return this.modes.find((m) => m.isDefault)?.label ?? 'the system default';
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    this.dialogRef.close(this.selectedCode);
  }
}
